import { useParams, Link } from 'react-router-dom';
import { movies, categories } from '@/mocks/movies';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieGrid from '@/pages/home/components/MovieGrid';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const movie = movies.find((m) => m.id === Number(id));

  if (!movie) {
    return (
      <main className="pt-16">
        <div className="flex flex-col items-center justify-center py-20">
          <i className="ri-error-warning-line text-5xl text-foreground-300 mb-4"></i>
          <h2 className="font-heading text-xl font-bold text-foreground-700 mb-2">内容不存在</h2>
          <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 cursor-pointer">返回首页</Link>
        </div>
      </main>
    );
  }

  const category = categories.find((c) => c.slug === movie.category);
  const relatedMovies = movies.filter((m) => m.category === movie.category && m.id !== movie.id).slice(0, 6);

  return (
    <main className="pt-16">
      <Breadcrumb
        items={[
          { label: '首页', href: '/' },
          { label: category?.name || '全部', href: `/category/${movie.category}` },
          { label: movie.title },
        ]}
      />

      <article className="px-4 md:px-6 py-6 md:py-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-8 md:gap-10">
            <div className="lg:w-[300px] flex-shrink-0">
              <div className="sticky top-24">
                <div className="aspect-[3/4] rounded-xl overflow-hidden bg-background-100 mb-4">
                  <img
                    src={movie.coverImage}
                    alt={movie.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <Link
                  to={`/category/${movie.category}`}
                  className="block w-full py-3 bg-primary-500 text-background-50 rounded-full text-center font-medium text-sm cursor-pointer hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  <i className="ri-play-fill mr-1"></i>
                  立即观看
                </Link>
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="font-heading text-xl md:text-3xl font-bold text-foreground-950 mb-1">
                {movie.title}
              </h1>
              <p className="text-sm text-foreground-500 mb-5">{movie.subtitle}</p>

              <div className="flex flex-wrap items-center gap-2 mb-6">
                <span className="px-3 py-1 text-xs font-medium bg-primary-100/70 text-primary-700 rounded-full whitespace-nowrap">
                  {movie.quality}
                </span>
                <span className="px-3 py-1 text-xs font-medium bg-accent-100/80 text-accent-700 rounded-full whitespace-nowrap">
                  {movie.rating} 分
                </span>
                {movie.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 text-xs font-medium bg-secondary-100 text-secondary-700 rounded-full whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6 p-4 bg-background-100 rounded-xl">
                {[
                  { label: '导演', value: movie.director },
                  { label: '主演', value: movie.actors.join(' / ') },
                  { label: '地区', value: movie.region },
                  { label: '时长', value: movie.duration },
                  { label: '上映日期', value: movie.releaseDate },
                  { label: '播放量', value: movie.playCount + ' 次' },
                ].map((info) => (
                  <div key={info.label}>
                    <span className="block text-xs text-foreground-500 mb-0.5">{info.label}</span>
                    <span className="text-sm text-foreground-900 font-medium">{info.value}</span>
                  </div>
                ))}
              </div>

              <div className="mb-8">
                <h2 className="font-heading text-lg font-bold text-foreground-950 mb-3">剧情简介</h2>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed whitespace-pre-line">
                  {movie.content}
                </p>
              </div>

              {movie.stills.length > 0 && (
                <div className="mb-8">
                  <h2 className="font-heading text-lg font-bold text-foreground-950 mb-4">精彩剧照</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {movie.stills.map((still, idx) => (
                      <div key={idx} className="aspect-video rounded-lg overflow-hidden bg-background-100">
                        <img
                          src={still}
                          alt={`${movie.title} 剧照 ${idx + 1}`}
                          className="w-full h-full object-cover hover:scale-105 transition-transform duration-500 cursor-pointer"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </article>

      {relatedMovies.length > 0 && (
        <MovieGrid
          title="相关推荐"
          movies={relatedMovies}
          viewAllLink={`/category/${movie.category}`}
          viewAllText="查看更多"
          accentColor="accent"
        />
      )}

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Movie",
            "name": movie.title,
            "description": movie.description,
            "image": movie.coverImage,
            "dateCreated": movie.releaseDate,
            "director": {
              "@type": "Person",
              "name": movie.director
            },
            "actor": movie.actors.map((a) => ({ "@type": "Person", "name": a })),
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": movie.rating,
              "bestRating": "10"
            }
          })
        }}
      />
    </main>
  );
}